import { z } from 'zod';
import { ISO_CURRENCY } from './proposal.js';

// Payments. One per attempt — a single Invoice can accumulate many Payment
// rows (pending → succeeded/failed for cards, plus a refund row referencing
// the original via parent_payment_id). The card flow flips status via the
// Revolut webhook (Phase 5c); wires are reconciled by Finance in
// POST /api/invoices/:id/mark-paid which writes a method=wire row directly.

// How the funds moved.
//   card — Revolut Business hosted checkout
//   wire — BACS / SWIFT bank transfer, reconciled manually
export const PAYMENT_METHODS = ['card', 'wire'] as const;
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

// Card networks Omega accepts (REQ-010). Validated server-side both at order
// creation (Phase 5b) and at webhook receipt (defence in depth — Phase 5c).
export const CARD_NETWORKS = ['amex', 'visa', 'mastercard', 'unionpay'] as const;
export const CardNetworkSchema = z.enum(CARD_NETWORKS);
export type CardNetwork = z.infer<typeof CardNetworkSchema>;

// Lifecycle.
//   pending   — created; awaiting Revolut redirect / webhook
//   succeeded — Revolut confirmed funds OR Finance reconciled a wire
//   failed    — Revolut returned failure for the attempt
//   refunded  — refund record (parent_payment_id points to the original)
export const PAYMENT_STATUSES = ['pending', 'succeeded', 'failed', 'refunded'] as const;
export const PaymentStatusSchema = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

// Public DTO. Embedded on InvoiceDto when GET /api/invoices/:id fetches an
// invoice — clients see their own payments timeline; staff see all rows.
// Money fields are integer minor units (REQ-010); currency is ISO 4217.
export const PaymentDtoSchema = z.object({
  id: z.string(),                                   // PAY-XXXX
  invoice_id: z.string(),
  client_id: z.string(),

  method: PaymentMethodSchema,
  card_network: CardNetworkSchema.nullable().optional(),
  card_last4: z.string().nullable().optional(),

  amount_minor: z.number().int().nonnegative(),
  currency: z.enum(ISO_CURRENCY),

  status: PaymentStatusSchema,

  external_ref: z.string().nullable().optional(),
  failure_code: z.string().nullable().optional(),
  failure_message: z.string().nullable().optional(),

  parent_payment_id: z.string().nullable().optional(),
  refunded_at: z.string().nullable().optional(),
  refund_amount_minor: z.number().int().nonnegative().nullable().optional(),
  refund_reason: z.string().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type PaymentDto = z.infer<typeof PaymentDtoSchema>;

// Body for POST /api/invoices/:id/pay-card (Phase 5c). Single field — the
// client just signals consent to be redirected to Revolut. The server pulls
// amount + currency from the Invoice; nothing client-controlled.
export const StartCardPaymentInputSchema = z.object({
  consent_descriptor: z.literal(true, {
    errorMap: () => ({ message: 'Acknowledge descriptor' }),
  }),
});
export type StartCardPaymentInput = z.infer<typeof StartCardPaymentInputSchema>;

// Body for POST /api/invoices/:id/refund (Phase 5f). amount_minor omitted =
// full refund of the original payment.
export const RefundInputSchema = z.object({
  amount_minor: z.number().int().positive().optional(),
  reason: z.string().min(1).max(500),
});
export type RefundInput = z.infer<typeof RefundInputSchema>;
